//Marketing/AlertasManager.js

import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Image as ImageIcon, X, AlertTriangle, Megaphone, Edit, Calendar, Link as LinkIcon } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

export default function AlertasManager({ estabelecimentoId }) {
    const [alertas, setAlertas] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados do Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editId, setEditId] = useState(null); // NULL = Novo, ID = Editando
    
    // Estados do Formulário
    const [titulo, setTitulo] = useState('');
    const [mensagem, setMensagem] = useState('');
    const [imagem, setImagem] = useState(null);
    const [imagemPreview, setImagemPreview] = useState(null);
    const [ativo, setAtivo] = useState(true);
    
    // Estados: Agendamento e Ação
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [textoBotao, setTextoBotao] = useState('Saiba mais');
    const [linkAcao, setLinkAcao] = useState('');

    const fileInputRef = useRef(null);
    const BASE_URL = 'https://app.universomidiadigital.com.br';

    useEffect(() => {
        carregarAlertas();
    }, [estabelecimentoId]);

    const carregarAlertas = async () => {
        if (!estabelecimentoId) return;
        try {
            setLoading(true);
            const res = await api.get(`/marketing/${estabelecimentoId}/alertas`);
            setAlertas(res.data || []);
        } catch (error) {
            console.error("Erro ao carregar alertas:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImagem(file);
            setImagemPreview(URL.createObjectURL(file));
        }
    };

    const openModal = (alerta = null) => {
        if (alerta) {
            // Modo Edição
            setEditId(alerta.id);
            setTitulo(alerta.titulo || '');
            setMensagem(alerta.mensagem || '');
            setAtivo(alerta.ativo === 1);
            setImagemPreview(alerta.imagem_url ? (alerta.imagem_url.startsWith('http') ? alerta.imagem_url : `${BASE_URL}${alerta.imagem_url}`) : null);
            
            // Tratamento seguro para evitar crash em datas inválidas/nulas
            try {
                setDataInicio(alerta.data_inicio ? new Date(alerta.data_inicio).toISOString().slice(0,16) : '');
            } catch { setDataInicio(''); }

            try {
                setDataFim(alerta.data_fim ? new Date(alerta.data_fim).toISOString().slice(0,16) : '');
            } catch { setDataFim(''); }

            setTextoBotao(alerta.texto_botao || 'Saiba mais');
            setLinkAcao(alerta.link_acao || '');
        } else {
            // Modo Criação
            setEditId(null);
            setTitulo('');
            setMensagem('');
            setImagem(null);
            setImagemPreview(null);
            setAtivo(true);
            setDataInicio('');
            setDataFim('');
            setTextoBotao('Saiba mais');
            setLinkAcao('');
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setImagem(null);
        setImagemPreview(null);
        setEditId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!titulo || !mensagem) {
            return toast.error("Título e mensagem são obrigatórios!");
        }

        setIsSubmitting(true);
        const loadingToast = toast.loading(editId ? "Atualizando alerta..." : "Salvando alerta...");

        try {
            const formData = new FormData();
            formData.append('estabelecimento_id', estabelecimentoId);
            formData.append('titulo', titulo);
            formData.append('mensagem', mensagem);
            formData.append('ativo', ativo ? 1 : 0);
            formData.append('texto_botao', textoBotao);
            formData.append('link_acao', linkAcao);
            
            if (dataInicio) formData.append('data_inicio', dataInicio);
            if (dataFim) formData.append('data_fim', dataFim);
            if (imagem) formData.append('imagem', imagem);

            if (editId) {
                // Rota corrigida para edição passando estabelecimento e ID do alerta caso necessário
                await api.put(`/marketing/${estabelecimentoId}/alertas/${editId}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                // Rota de POST para criação
                await api.post(`/marketing/${estabelecimentoId}/alertas`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            toast.dismiss(loadingToast);
            toast.success(editId ? "Alerta atualizado com sucesso!" : "Alerta salvo com sucesso!");
            closeModal();
            carregarAlertas();
        } catch (error) {
            toast.dismiss(loadingToast);
            console.error("Erro ao salvar alerta:", error);
            toast.error(error.response?.data?.error || "Erro ao salvar o alerta.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir este alerta?")) return;

        const loadingToast = toast.loading("Excluindo...");
        try {
            await api.delete(`/marketing/alertas/${id}`);
            toast.dismiss(loadingToast);
            toast.success("Alerta excluído!");
            setAlertas(alertas.filter(a => a.id !== id));
        } catch (error) {
            toast.dismiss(loadingToast);
            console.error("Erro ao excluir alerta:", error);
            toast.error("Erro ao excluir o alerta.");
        }
    };

    const toggleStatus = async (alerta) => {
        const novoStatus = alerta.ativo === 1 ? 0 : 1;
        try {
            await api.put(`/marketing/alertas/${alerta.id}/status`, { ativo: novoStatus });
            setAlertas(alertas.map(a => 
                a.id === alerta.id ? { ...a, ativo: novoStatus } : a
            ));
            toast.success(novoStatus === 1 ? "Alerta ativado!" : "Alerta desativado!");
        } catch (error) {
            console.error("Erro ao alterar status:", error);
            toast.error("Erro ao alterar status.");
        }
    };

    if (loading) {
        return <div className="loader-container"><div className="spinner"></div></div>;
    }

    return (
        <div className="marketing-section-container animate-fade-in">
            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                        <Megaphone size={28} color="#ea1d2c" />
                        Alertas Popup
                    </h2>
                    <p style={{ color: '#64748b', margin: '5px 0 0 0', fontSize: '14px' }}>
                        Gerencie avisos, crie agendamentos e acompanhe conversões.
                    </p>
                </div>
                <button 
                    onClick={() => openModal()}
                    style={{ backgroundColor: '#ea1d2c', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    <Plus size={20} /> Novo Alerta
                </button>
            </div>

            {alertas.length === 0 ? (
                <div className="empty-state" style={{ textAlign: 'center', padding: '50px 20px', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                    <AlertTriangle size={48} color="#94a3b8" style={{ marginBottom: '15px' }} />
                    <h3 style={{ margin: '0 0 10px 0', color: '#334155' }}>Nenhum alerta configurado</h3>
                    <p style={{ color: '#64748b', margin: 0 }}>Crie seu primeiro alerta para avisar seus clientes sobre promoções, horários ou novidades.</p>
                </div>
            ) : (
                <div className="alerts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {alertas.map(alerta => (
                        <div key={alerta.id} className="alert-card" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                            {alerta.imagem_url && (
                                <div style={{ height: '140px', overflow: 'hidden' }}>
                                    <img 
                                        src={alerta.imagem_url.startsWith('http') ? alerta.imagem_url : `${BASE_URL}${alerta.imagem_url}`} 
                                        alt="Alerta" 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                </div>
                            )}
                            <div style={{ padding: '15px', flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                    <h4 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>{alerta.titulo}</h4>
                                    <span style={{ 
                                        padding: '4px 8px', 
                                        borderRadius: '20px', 
                                        fontSize: '11px', 
                                        fontWeight: 'bold',
                                        backgroundColor: alerta.ativo ? '#dcfce7' : '#f1f5f9',
                                        color: alerta.ativo ? '#166534' : '#64748b'
                                    }}>
                                        {alerta.ativo ? 'ATIVO' : 'INATIVO'}
                                    </span>
                                </div>
                                <p style={{ margin: '0 0 15px 0', fontSize: '13px', color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {alerta.mensagem}
                                </p>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '12px', color: '#94a3b8' }}>
                                    {(alerta.data_inicio || alerta.data_fim) && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <Calendar size={14} /> Agendado
                                        </div>
                                    )}
                                    {alerta.link_acao && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <LinkIcon size={14} /> Possui link de ação
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 15px', display: 'flex', justifyContent: 'space-between', background: '#f8fafc' }}>
                                <button 
                                    onClick={() => toggleStatus(alerta)}
                                    style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', color: alerta.ativo ? '#f59e0b' : '#10b981', fontWeight: '600' }}
                                >
                                    {alerta.ativo ? 'Desativar' : 'Ativar'}
                                </button>
                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <button 
                                        onClick={() => openModal(alerta)}
                                        style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', color: '#3b82f6' }}
                                    >
                                        <Edit size={16} /> Editar
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(alerta.id)}
                                        style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', color: '#ef4444' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* MODAL DE CRIAÇÃO / EDIÇÃO */}
            {isModalOpen && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="modal-content" style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', padding: '24px', position: 'relative' }}>
                        <button onClick={closeModal} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', cursor: 'pointer' }}>
                            <X size={24} color="#64748b" />
                        </button>
                        
                        <h3 style={{ margin: '0 0 20px 0' }}>{editId ? 'Editar Alerta' : 'Novo Alerta Popup'}</h3>
                        
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Título (Obrigatório)</label>
                                <input 
                                    type="text" 
                                    value={titulo} 
                                    onChange={e => setTitulo(e.target.value)}
                                    placeholder="Ex: Comunicado Importante!"
                                    style={{ width: '100%', padding: '10px 15px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                                    required
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Mensagem (Obrigatória)</label>
                                <textarea 
                                    value={mensagem} 
                                    onChange={e => setMensagem(e.target.value)}
                                    placeholder="Digite a mensagem que aparecerá para o cliente..."
                                    style={{ width: '100%', padding: '10px 15px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', minHeight: '80px', resize: 'vertical' }}
                                    required
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Data de Início (Opcional)</label>
                                    <input 
                                        type="datetime-local" 
                                        value={dataInicio} 
                                        onChange={e => setDataInicio(e.target.value)}
                                        style={{ width: '100%', padding: '10px 15px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Data de Fim (Opcional)</label>
                                    <input 
                                        type="datetime-local" 
                                        value={dataFim} 
                                        onChange={e => setDataFim(e.target.value)}
                                        style={{ width: '100%', padding: '10px 15px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Texto do Botão</label>
                                    <input 
                                        type="text" 
                                        value={textoBotao} 
                                        onChange={e => setTextoBotao(e.target.value)}
                                        placeholder="Ex: Saiba mais"
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Link de Ação (Para gerar lead/clique)</label>
                                    <input 
                                        type="url" 
                                        value={linkAcao} 
                                        onChange={e => setLinkAcao(e.target.value)}
                                        placeholder="Ex: https://wa.me/5511999999999"
                                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#475569' }}>Imagem (Opcional)</label>
                                <div 
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ 
                                        border: '2px dashed #cbd5e1', 
                                        borderRadius: '8px', 
                                        padding: '20px', 
                                        textAlign: 'center', 
                                        cursor: 'pointer',
                                        background: '#f8fafc',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '150px'
                                    }}
                                >
                                    {imagemPreview ? (
                                        <img src={imagemPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '4px', objectFit: 'contain' }} />
                                    ) : (
                                        <>
                                            <ImageIcon size={32} color="#94a3b8" style={{ marginBottom: '10px' }} />
                                            <span style={{ color: '#64748b', fontSize: '14px' }}>Clique para selecionar uma imagem</span>
                                        </>
                                    )}
                                </div>
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleImageSelect} 
                                    accept="image/*" 
                                    style={{ display: 'none' }} 
                                />
                            </div>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '10px' }}>
                                <input 
                                    type="checkbox" 
                                    checked={ativo} 
                                    onChange={e => setAtivo(e.target.checked)} 
                                    style={{ width: '18px', height: '18px' }}
                                />
                                <span style={{ fontWeight: '500', color: '#334155' }}>Ativar alerta imediatamente</span>
                            </label>

                            <button 
                                type="submit" 
                                disabled={isSubmitting}
                                style={{ 
                                    backgroundColor: '#ea1d2c', 
                                    color: '#fff', 
                                    border: 'none', 
                                    padding: '12px', 
                                    borderRadius: '8px', 
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                    opacity: isSubmitting ? 0.7 : 1,
                                    marginTop: '10px'
                                }}
                            >
                                {isSubmitting ? 'Salvando...' : (editId ? 'Atualizar Alerta' : 'Salvar Alerta')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}